/**
 * Telegram configuration access for the Settings modal (#52-#55).
 *
 * Same shape as `useNotion`: one query key, and every mutation that changes the binding answers
 * with the full #52 payload, so the cache is replaced rather than invalidated.
 *
 * The one thing it adds is the **handshake poll**. Binding happens on Telegram's side — the user
 * opens the chat and sends the code — and there is no event to ride, so while `pending` is set the
 * tab asks #52 every couple of seconds and flips to connected on its own.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { TelegramStateResponse } from '@lw/shared';
import { connectTelegram, disconnectTelegram, getTelegram, testTelegram } from '../../api/telegram';
import { QK } from '../../api/queryKeys';

/** Poll cadence while a code is outstanding (SPEC § Connection handshake step 3). */
export const TELEGRAM_PENDING_POLL_MS = 2000;

export function useTelegramQuery(enabled = true): UseQueryResult<TelegramStateResponse, Error> {
  return useQuery({
    queryKey: QK.telegram,
    queryFn: ({ signal }) => getTelegram(signal),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.pending == null ? false : TELEGRAM_PENDING_POLL_MS,
  });
}

function useTelegramMutation<TVars>(mutationFn: (vars: TVars) => Promise<TelegramStateResponse>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (state) => {
      queryClient.setQueryData(QK.telegram, state);
    },
  });
}

export function useConnectTelegram() {
  return useTelegramMutation((token: string) => connectTelegram(token));
}

export function useTestTelegram() {
  return useTelegramMutation((_vars: void) => testTelegram());
}

/** #54 answers `204`, so the cache is refetched rather than replaced. */
export function useDisconnectTelegram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectTelegram(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QK.telegram }),
  });
}
